import React, { useEffect, useState } from 'react';
import { useLanguage } from '../i18n';
import { enablePush, getPushStatus, getLastPushError } from '../push';

const DISMISS_KEY = 'veylPushPromptDismissed';

function readDismissed() {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
}

function saveDismissed() {
  try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* storage unavailable */ }
}

// True when Veyl is running as an installed app (home screen / app window).
function isInstalledApp() {
  if (typeof window === 'undefined') return false;
  return (
    window.navigator.standalone === true ||
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
  );
}

// True on an iPhone/iPad where Veyl is open in Safari rather than installed
// to the Home Screen. iOS only allows web push for installed apps.
function isIosNotInstalled() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  const isIos = /iPhone|iPad|iPod/i.test(ua);
  return isIos && !isInstalledApp();
}

export default function PushPrompt({ t, showDenied = false }) {
  const { t: translate } = useLanguage();
  // translate() returns the key itself on a miss, so check for that.
  const tr = (key, fallback) => {
    const v = translate(key);
    return !v || v === key ? fallback : v;
  };

  const [status, setStatus] = useState('loading');
  const [dismissed, setDismissed] = useState(readDismissed);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPushStatus().then((s) => { if (!cancelled) setStatus(s); });
    return () => { cancelled = true; };
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    setFailed(false);
    const result = await enablePush();
    setBusy(false);
    if (result === 'error') setFailed(true);
    else setStatus(result);
  };

  const handleDismiss = () => {
    saveDismissed();
    setDismissed(true);
  };

  const boxStyle = {
    backgroundColor: t.cardBg,
    border: `1px solid ${t.cardBorder}`,
    borderRadius: '0.75rem',
    padding: '1rem 1.25rem',
    marginBottom: '1.25rem',
  };
  const titleStyle = { fontSize: '0.9rem', fontWeight: '700', color: t.text, marginBottom: '0.35rem' };
  const bodyStyle = { fontSize: '0.8rem', color: t.subtext, lineHeight: 1.5 };

  if (status === 'loading' || status === 'granted') return null;

  // iPhone with Veyl not installed: push isn't possible yet.
  if (status === 'unsupported') {
    if (!isIosNotInstalled() || dismissed) return null;
    return (
      <div style={boxStyle}>
        <div style={titleStyle}>{tr('pushIosTitle', 'Get notifications on iPhone')}</div>
        <div style={bodyStyle}>
          {tr('pushIosBody', 'Add Veyl to your Home Screen first: tap Share, then Add to Home Screen, then open Veyl from there and turn on notifications.')}
        </div>
        <button
          onClick={handleDismiss}
          style={{ marginTop: '0.75rem', background: 'none', border: 'none', color: t.subtext, cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}
        >
          {tr('pushNotNow', 'Not now')}
        </button>
      </div>
    );
  }

  // Blocked in the browser: only explain on the Notifications tab.
  if (status === 'denied') {
    if (!showDenied) return null;
    return (
      <div style={boxStyle}>
        <div style={titleStyle}>{tr('pushBlockedTitle', 'Notifications are blocked')}</div>
        <div style={bodyStyle}>
          {tr('pushBlockedBody', "Your browser is blocking notifications from Veyl. To turn them on, open your browser's site settings for veyl.space, allow notifications, then reload the page.")}
        </div>
      </div>
    );
  }

  // status === 'default': the user hasn't been asked yet.
  if (dismissed) return null;

  return (
    <div style={boxStyle}>
      <div style={titleStyle}>{tr('pushPromptTitle', 'Get notified when Veyl is closed')}</div>
      <div style={bodyStyle}>
        {tr('pushPromptBody', "We'll let you know when you're matched or your partner messages you. Notifications never include message content.")}
      </div>
      {failed && (
        <div style={{ ...bodyStyle, color: '#f87171', marginTop: '0.5rem' }}>
          {tr('pushError', 'Something went wrong turning notifications on. Please try again.')}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.75rem' }}>
        <button
          onClick={handleEnable}
          disabled={busy}
          style={{
            background: 'linear-gradient(135deg, #008081, #4B2C5E)',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '0.5rem',
            padding: '0.5rem 1rem',
            fontSize: '0.8rem',
            fontWeight: '600',
            cursor: busy ? 'default' : 'pointer',
            opacity: busy ? 0.7 : 1,
          }}
        >
          {tr('pushEnable', 'Turn on notifications')}
        </button>
        <button
          onClick={handleDismiss}
          style={{ background: 'none', border: 'none', color: t.subtext, cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}
        >
          {tr('pushNotNow', 'Not now')}
        </button>
      </div>
    </div>
  );
}